import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

interface MissionsState {
  missionListSeed: string;
  rejectedValidationSeed: string;
  resetMap: boolean;
  recentlyAcceptedMission: string | undefined;
  branchId: string;
  lat: number;
  lng: number;
  completionPrompt: MissionCompletionPrompt | undefined;
  completionSessionVersion: number;
  completionUserId: string | undefined;
  showMissionListView: boolean;
  missionReferralMapTarget: MissionReferralMapTarget | undefined;
}

export interface MissionCompletionPrompt {
  missionId: string;
  userId: string;
  sessionVersion: number;
}

export interface MissionReferralMapTarget {
  token: string;
  branchId: string;
  periodMissionConfigurationId: string;
}

// Define the initial state using that type
const initialState: MissionsState = {
  missionListSeed: '12345',
  rejectedValidationSeed: '12345',
  resetMap: false,
  recentlyAcceptedMission: undefined,
  branchId: undefined,
  lat: undefined,
  lng: undefined,
  completionPrompt: undefined,
  completionSessionVersion: 0,
  completionUserId: undefined,
  showMissionListView: false,
  missionReferralMapTarget: undefined,
};

export const missionsSlice = createSlice({
  name: 'missions',
  initialState,
  reducers: {
    setReloadMissionListSeed: (state, action: PayloadAction<string>) => {
      state.missionListSeed = action.payload;
    },
    setReloadRejectedValidationSeed: (state, action: PayloadAction<string>) => {
      state.rejectedValidationSeed = action.payload;
    },
    setResetMap: (state, action: PayloadAction<boolean>) => {
      state.resetMap = action.payload;
    },
    queueMissionCompletionPrompt: (state, action: PayloadAction<MissionCompletionPrompt>) => {
      const prompt = action.payload;
      if (prompt.missionId && prompt.userId === state.completionUserId &&
          prompt.sessionVersion === state.completionSessionVersion) {
        state.completionPrompt = prompt;
      }
    },
    dismissMissionCompletionPrompt: (state, action: PayloadAction<MissionCompletionPrompt>) => {
      const prompt = state.completionPrompt;
      if (prompt?.missionId === action.payload.missionId && prompt.userId === action.payload.userId &&
          prompt.sessionVersion === action.payload.sessionVersion) {
        state.completionPrompt = undefined;
      }
    },
    setMissionAcceptedId: (state, action: PayloadAction<string | undefined>) => {
      state.recentlyAcceptedMission = action.payload;
    },
    setSelectedBranch: (state, action: PayloadAction<{ id: string; lat: number; lng: number }>) => {
      state.branchId = action.payload.id;
      state.lat = action.payload.lat;
      state.lng = action.payload.lng;
    },
    setShowMissionListView: (state, action: PayloadAction<boolean>) => {
      state.showMissionListView = action.payload;
    },
    setMissionReferralMapTarget: (state, action: PayloadAction<MissionReferralMapTarget | undefined>) => {
      state.missionReferralMapTarget = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase('auth/logout', (state) => {
      state.completionPrompt = undefined;
      state.completionUserId = undefined;
      state.completionSessionVersion++;
    });
    builder.addMatcher(
      (action) => action.type === 'auth/setLogged' || action.type === 'auth/setUser',
      (state, action: PayloadAction<any>) => {
        const user = action.type === 'auth/setLogged' ? action.payload?.user : action.payload;
        const userId = user && !user.isAnonymous &&
          (action.type !== 'auth/setLogged' || action.payload?.idToken) ? user.uid : undefined;
        if (userId !== state.completionUserId) {
          state.completionPrompt = undefined;
          state.completionUserId = userId;
          state.completionSessionVersion++;
        }
      },
    );
  },
});

export default missionsSlice.reducer;
export const missionsActions = missionsSlice.actions;
